import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Clock, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import RUCImage1 from "../../../assets/images/blog/visittorussia/DSC03355.JPG";
import RUCImage2 from "../../../assets/images/blog/visittorussia/DSC03649.JPG";
import RUCImage3 from "../../../assets/images/blog/visittorussia/DSC03682.JPG";
import RUCImage4 from "../../../assets/images/blog/visittorussia/DSC04266.JPG";
import RUCImage5 from "../../../assets/images/blog/visittorussia/DSC04368.JPG";

const RUCVisitPost = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <article className="w-full max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        <motion.button
          onClick={() => navigate("/articles/blog")}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center text-red-600 hover:text-red-700 mb-4 sm:mb-6 lg:mb-8 group text-sm sm:text-base"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
          Back to Blog
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl overflow-hidden">
            {/* Hero Image */}
            <div className="w-full h-48 sm:h-64 md:h-80 lg:h-96 overflow-hidden bg-gray-100">
              <img
                src={RUCImage1}
                alt="RUC Visit"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="p-4 sm:p-6 md:p-8 lg:p-12">
              {/* Meta Tags */}
              <div className="flex flex-wrap gap-2 sm:gap-3 lg:gap-4 mb-4 sm:mb-6">
                <span className="px-3 py-1 sm:px-4 sm:py-2 bg-red-100 text-red-600 rounded-full text-xs sm:text-sm font-medium">
                  Global Cooperation
                </span>
                <div className="flex items-center text-gray-600 text-xs sm:text-sm">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                  March 12, 2026
                </div>
                <div className="flex items-center text-gray-600 text-xs sm:text-sm">
                  <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                  10 min read
                </div>
                <div className="flex items-center text-gray-600 text-xs sm:text-sm">
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                  Mytishchi, Russia
                </div>
              </div>

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                Rais of Tatarstan Rustam Minnikhanov Visits Russian University of Cooperation and Launches "RUC–Infotech" Technopark
              </h1>

              {/* Content */}
              <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
                <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                  Today, Rustam Nurgalievich Minnikhanov, the Rais (Head) of the Republic of Tatarstan, paid a working visit to the Russian University of Cooperation (RUC). Accompanied by Dmitry Lvovich Zubov, Chairman of the Council of Centrosoyuz of the Russian Federation, and Alsu Rustemovna Nabieva, the university's Rector, the head of the republic toured the institution's key facilities.
                </p>

                <div className="my-4 sm:my-6 lg:my-8 rounded-lg sm:rounded-xl overflow-hidden shadow-md sm:shadow-lg">
                  <img src={RUCImage2} alt="Visit to Russian University of Cooperation" className="w-full h-auto" />
                </div>

                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4">Museum Tour and Historical Heritage</h2>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 mb-4 sm:mb-6">
                  The visit's agenda began with a tour of the university's museums. At the "Reality of Donbas" Museum—established by student volunteer Valery Kalinin and recognized as one of the finest university museums in Russia's Central Federal District—Rustam Minnikhanov was presented with unique artifacts brought back from the zone of the special military operation. Subsequently, the delegation visited the Central Museum of the History of Consumer Cooperation, whose exhibits chronicle the more than 195-year history of the cooperative movement in Russia—from the Decembrist <em>artels</em> to the present day.
                </p>

                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4">Youth Forum and Global Dialogue</h2>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 mb-4 sm:mb-6">
                  A highlight of the visit was the Rais of Tatarstan's participation in the youth track of the International Forum on Consumer Cooperation, which kicked off at the university's International Cultural Center. Dmitry Zubov and Rustam Minnikhanov engaged in dialogue with students representing initiatives within the cooperative education system—a network encompassing over 100,000 students nationwide. The leaders discussed with the youth the integration of modern technologies into the cooperative sector and the pivotal role of student-led projects in the industry's development.
                </p>

                <div className="my-4 sm:my-6 lg:my-8 rounded-lg sm:rounded-xl overflow-hidden shadow-md sm:shadow-lg">
                  <img src={RUCImage3} alt="Youth Forum Discussion" className="w-full h-auto" />
                </div>

                <div className="bg-red-50 border-l-4 border-red-600 p-4 sm:p-6 my-4 sm:my-6 lg:my-8 rounded-r-lg">
                  <p className="text-sm sm:text-base text-gray-800 italic">
                    "Upon graduating from this university, you will go on to become successful individuals in life. Our university is developing exceptionally well; new academic specializations are being introduced here. Who would have thought that consumer cooperation today is synonymous with modern technology? I am confident that our educational programs will be the best in the country."
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-2 font-medium">— Dmitry Zubov, Chairman of the Council of Centrosoyuz</p>
                </div>

                <div className="bg-red-50 border-l-4 border-red-600 p-4 sm:p-6 my-4 sm:my-6 lg:my-8 rounded-r-lg">
                  <p className="text-sm sm:text-base text-gray-800 italic">
                    "In the cooperative sector, people—our personnel—are paramount. Centrosoyuz has done a tremendous amount to ensure the system continues to grow. Here in Tatarstan, we fully support the cooperative movement."
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-2 font-medium">— Rustam Minnikhanov, Rais of Tatarstan</p>
                </div>

                <div className="my-4 sm:my-6 lg:my-8 rounded-lg sm:rounded-xl overflow-hidden shadow-md sm:shadow-lg">
                  <img src={RUCImage4} alt="Campus Innovation" className="w-full h-auto" />
                </div>

                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4">My Perspective as ICA Youth President</h2>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 mb-4 sm:mb-6">
                  As a special guest at the event, I had the privilege of sharing my vision on the role of youth in the cooperative movement. I emphasized that young leaders are the primary driving force behind the cooperative movement, bringing innovation, fresh perspectives, and digital expertise to traditional cooperative structures.
                </p>

                <div className="bg-blue-50 border-l-4 border-blue-600 p-4 sm:p-6 my-4 sm:my-6 lg:my-8 rounded-r-lg">
                  <p className="text-sm sm:text-base text-gray-800 italic">
                    "Young leaders are the primary driving force behind the cooperative movement."
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-2 font-medium">— Harsh Mukeshbhai Sanghani, President of the Youth Network of the International Co-operative Alliance</p>
                </div>

                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4">Campus Innovation Tour</h2>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 mb-4 sm:mb-6">
                  Continuing their tour of the campus, the delegation inspected the Investigative Committee Cadet Class, the media laboratory, the UAV center, and the facilities of the "Movement of the First" youth organization; they then visited the new Interactive Museum of Consumer Cooperation, which brings together 20 virtual exhibitions from regional consumer unions.
                </p>

                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4">Launch of RUC–Infotech Technopark</h2>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 mb-4 sm:mb-6">
                  The visit culminated in the grand opening of the Technopark for the IT company "RUC–Infotech." Established in Kazan in 2025 and holding resident status within the local IT Park, the company has now acquired a new dedicated space for its expansion. The project had previously received state accreditation from the Ministry of Digital Development, a distinction that opens up vast opportunities for software development and the digital transformation of educational processes.
                </p>

                <div className="my-4 sm:my-6 lg:my-8 rounded-lg sm:rounded-xl overflow-hidden shadow-md sm:shadow-lg">
                  <img src={RUCImage5} alt="Technopark Launch" className="w-full h-auto" />
                </div>

                <p className="text-sm sm:text-base lg:text-lg text-gray-700 mb-4 sm:mb-6">
                  Rustam Minnikhanov and Dmitry Zubov officially launched the Technopark, which is set to serve as a center of excellence for digitalization, robotics, and software development for all 15 of the university's branches—spanning the entire country from Kaliningrad to Kamchatka.
                </p>

                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4">Technopark Features and Opportunities</h2>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 mb-4 sm:mb-6">
                  Infotech is a multifunctional facility featuring a spacious co-working area ideal for laptop work, a cozy lounge area, and dedicated meeting rooms. A separate zone for esports has also been set up; equipped with high-performance gaming computers, the area allows students to train for competitions, and a PlayStation 5 console is set to be added very soon.
                </p>

                <p className="text-sm sm:text-base lg:text-lg text-gray-700 mb-4 sm:mb-6">
                  Most importantly, however, Infotech opens up tangible career prospects for students. An IT company office is being expanded on the university's premises: RUK-Infotech will hire students to work directly on campus. These students will focus on the university's digitalization as well as on projects with partner companies involving web development, 1C programming, robotics, and unmanned systems.
                </p>

                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4">Looking Ahead: International Forum on Consumer Cooperation</h2>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 mb-4 sm:mb-6">
                  It is worth noting that today's visit took place in anticipation of another significant event: the start of the International Forum on Consumer Cooperation, now just 119 days away. The Forum is scheduled to be held in Kazan from July 8 to July 10, 2026. Timed to coincide with the 195th anniversary of the consumer cooperation movement in Russia and the 110th anniversary of Tatpotrebsoyuz, the Forum will bring together participants from 50 countries around the world.
                </p>

                <p className="text-sm sm:text-base lg:text-lg text-gray-700 mb-4 sm:mb-6">
                  In essence, today in Mytishchi marked the symbolic launch of the countdown to the year's premier cooperative event, which will be hosted by the capital of Tatarstan.
                </p>

                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4">Special Recognition</h2>
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 mb-4 sm:mb-6">
                  Concluding the visit, Dmitry Zubov, Chairman of the Council of Centrosoyuz, and Rustam Minnikhanov, Rais of Tatarstan, warmly congratulated RUK Rector Alsu Rustemovna Nabieva on her birthday, acknowledging her significant contribution to the advancement of cooperative education and the strengthening of ties between the university and Russia's regions.
                </p>

                <div className="bg-gray-100 border-l-4 border-gray-600 p-4 sm:p-6 my-4 sm:my-6 lg:my-8 rounded-r-lg">
                  <p className="text-sm sm:text-base text-gray-800 italic">
                    "Our leaders are the people we look up to!"
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-2 font-medium">— Alsu Nabieva, RUK Rector</p>
                </div>

                <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-gray-200">
                  <p className="text-sm sm:text-base text-gray-600 italic">
                    This visit represents a significant milestone in the integration of technology and cooperative education, demonstrating how traditional cooperative values can be enhanced through digital innovation and youth leadership.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </article>
    </div>
  );
};

export default RUCVisitPost;
